import Vue from 'vue'

const createQueueId = () =>
  `${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`

const isOnline = () =>
  typeof navigator === 'undefined' ? true : navigator.onLine !== false

const defaultRecentSelections = () => ({
  client: [],
  employee: [],
  product: [],
})

export const state = () => ({
  forms: {},
  responsesQueue: {},
  recentSelections: defaultRecentSelections(),
  // IDs dos formulários respondidos localmente (online ou offline).
  // Persistido no LocalForage para filtragem offline correta na lista de disponíveis.
  answeredFormIds: [],
})

export const getters = {
  formById: state => id => {
    if (!id) return null
    return state.forms[id] || null
  },
  queuedResponses: state => Object.values(state.responsesQueue || {}),
  recentSelectionsByType: state => type => state.recentSelections?.[type] ?? [],
}

export const mutations = {
  UPSERT_FORM(state, payload) {
    const { formId, form, cachedAt } = payload
    if (!formId || !form) return
    Vue.set(state.forms, formId, {
      ...form,
      cachedAt: cachedAt || new Date().toISOString(),
    })
  },
  QUEUE_RESPONSE(state, payload) {
    const { id } = payload
    if (!id) return
    Vue.set(state.responsesQueue, id, payload)
  },
  REMOVE_RESPONSE(state, id) {
    if (!id) return
    Vue.delete(state.responsesQueue, id)
  },
  MARK_RESPONSE_SYNCED(state, payload) {
    const { id, syncedAt } = payload
    if (!id || !state.responsesQueue[id]) return
    Vue.set(state.responsesQueue[id], 'sync', true)
    Vue.set(
      state.responsesQueue[id],
      'syncedAt',
      syncedAt || new Date().toISOString()
    )
  },
  PUSH_RECENT_SELECTION(state, payload) {
    if (!payload) return
    const { type, selection } = payload
    if (!type || !selection) return
    const normalizedType = type.toLowerCase()
    if (!state.recentSelections[normalizedType]) {
      Vue.set(state.recentSelections, normalizedType, [])
    }
    const list = state.recentSelections[normalizedType] || []
    const id =
      selection.id || selection.code || selection.name || selection.label
    if (!id) return
    const sanitized = {
      ...selection,
      id,
      savedAt: new Date().toISOString(),
    }
    const existing = list.filter(item => item.id !== sanitized.id)
    const next = [sanitized, ...existing].slice(0, 20)
    Vue.set(state.recentSelections, normalizedType, next)
  },
  MARK_FORM_ANSWERED(state, formId) {
    if (!formId || state.answeredFormIds.includes(formId)) return
    state.answeredFormIds = [formId, ...state.answeredFormIds]
  },
  RESET(state) {
    state.forms = {}
    state.responsesQueue = {}
    state.recentSelections = defaultRecentSelections()
    state.answeredFormIds = []
  },
}

export const actions = {
  saveForm({ commit }, payload) {
    if (!payload) return
    commit('UPSERT_FORM', payload)
  },
  async queueResponse({ commit }, payload) {
    const id = payload.id || createQueueId()
    const now = new Date().toISOString()
    commit('QUEUE_RESPONSE', {
      ...payload,
      id,
      sync: false,
      createdAt: payload.createdAt || now,
      updatedAt: now,
    })
    if (payload.metadata?.audience?.type) {
      commit('PUSH_RECENT_SELECTION', {
        type: payload.metadata.audience.type,
        selection: payload.metadata.audience,
      })
    }
    return id
  },
  async submitResponse({ dispatch, commit }, payload) {
    const online = isOnline()
    const queuePayload = {
      formId: payload.formId,
      body: payload.body,
      metadata: payload.metadata || {},
    }
    if (!online) {
      const id = await dispatch('queueResponse', queuePayload)
      // Registra o formId como respondido para filtrar na lista offline
      commit('MARK_FORM_ANSWERED', payload.formId)
      return { queued: true, id }
    }
    try {
      await this.$axios.post(
        `/v3/app/researchform/${payload.formId}/answer`,
        queuePayload.body
      )
      // Registra o formId como respondido (envio online bem-sucedido)
      commit('MARK_FORM_ANSWERED', payload.formId)
      if (queuePayload.metadata?.audience?.type) {
        commit('PUSH_RECENT_SELECTION', {
          type: queuePayload.metadata.audience.type,
          selection: queuePayload.metadata.audience,
        })
      }
      return { queued: false }
    } catch (error) {
      const shouldQueue =
        !isOnline() ||
        !error?.response ||
        ['5', '0'].includes(
          error?.response?.status?.toString()?.charAt(0) ?? ''
        )
      if (shouldQueue) {
        const id = await dispatch('queueResponse', queuePayload)
        return { queued: true, id, error }
      }
      throw error
    }
  },
  async sendQueuedResponse(_, payload) {
    return this.$axios.post(
      `/v3/app/researchform/${payload.formId}/answer`,
      payload.body
    )
  },
  async serverSync({ state, commit, dispatch }) {
    if (!isOnline()) return false
    const queue = Object.values(state.responsesQueue || {}).filter(
      item => !item.sync
    )
    if (!queue.length) {
      commit('offline/UPDATE', 'research', { root: true })
      return null
    }
    commit('offline/UPDATING', 'research', { root: true })
    try {
      for (const item of queue) {
        await dispatch('sendQueuedResponse', item)
        commit('MARK_RESPONSE_SYNCED', { id: item.id })
        commit('REMOVE_RESPONSE', item.id)
        // Garante que o formId conste como respondido após sync bem-sucedido
        commit('MARK_FORM_ANSWERED', item.formId)
      }
      commit('offline/UPDATE', 'research', { root: true })
      return true
    } catch (error) {
      commit('offline/FAILED', 'research', { root: true })
      if (typeof window !== 'undefined') {
        window.$nuxt?.$rollbar?.error?.(error)
      }
      return false
    }
  },
}
